import React, { useState, useEffect } from 'react';
import {
    Container,
    TextField,
    Button,
    Typography,
    Avatar,
    Box,
    Paper,
    Grid,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@mui/material';
import Flag from 'react-world-flags';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { PhotoCamera, Clear } from '@mui/icons-material';
import isoLanguages from './isoLanguages';

const styles = {
    paper: { padding: '30px', borderRadius: '10px', position: 'relative' },
    avatarBox: { textAlign: 'center', mb: 2 },
    avatarSize: { width: 100, height: 100, margin: 'auto' },
    button: {
        borderRadius: '8px',
        backgroundColor: '#B5EAD7',
        color: 'black',
        textTransform: 'none',
        px: 2,
        '&:hover': { backgroundColor: '#A0D5C2' },
    },
    cancelIcon: { position: 'absolute', top: 16, right: 16 },
    sectionHeader: { mt: 4, mb: 2, fontWeight: 600 },
};

const ProfilePage = () => {
    const token = useSelector((state) => state.auth.token);

    const [profileData, setProfileData] = useState({
        _id: '',
        email: '',
        company: '',
        firstName: '',
        lastName: '',
        avatarUrl: '',
        occupation: '',
        linkedInProfile: '',   // <-- renamed
        website: '',
        twitter: '',
        github: '',
        instagram: '',
        translatedLanguages: '',
        availability: '',
    });
    const [avatarPreview, setAvatarPreview] = useState('');
    const [avatarFile, setAvatarFile] = useState(null);
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [fetching, setFetching] = useState(true);
    const [saving, setSaving] = useState(false);

    // Utility to validate URLs
    const isValidURL = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    // Fetch profile data
    useEffect(() => {
        const fetchProfileData = async () => {
            setFetching(true);
            try {
                const response = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const profile = response.data;

                setProfileData({
                    _id: profile._id || '',
                    email: profile.email || '',
                    company: profile.company || '',
                    firstName: profile.firstName || '',
                    lastName: profile.lastName || '',
                    avatarUrl: profile.avatarUrl || '',
                    occupation: profile.occupation || '',
                    linkedInProfile: profile.linkedInProfile || '',  // <-- use linkedInProfile
                    website: profile.website || '',
                    twitter: profile.twitter || '',
                    github: profile.github || '',
                    instagram: profile.instagram || '',
                    translatedLanguages: Array.isArray(profile.translatedLanguages)
                        ? profile.translatedLanguages[0]?.toLowerCase() || ''
                        : profile.translatedLanguages?.toLowerCase() || '',
                    availability: profile.availability || '',
                });
            } catch (error) {
                console.error('Error fetching profile data:', error);
            } finally {
                setFetching(false);
            }
        };
        fetchProfileData();
    }, [token]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setProfileData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    // Handle avatar file selection
    const handleAvatarUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setAvatarPreview(reader.result);
            };
            reader.readAsDataURL(file);
            setAvatarFile(file);
        }
    };

    // Validate inputs before saving
    const validate = () => {
        const newErrors = {};

        if (!profileData.firstName.trim()) {
            newErrors.firstName = 'First name is required.';
        }
        if (!profileData.lastName.trim()) {
            newErrors.lastName = 'Last name is required.';
        }
        if (
            profileData.linkedInProfile &&
            !isValidURL(profileData.linkedInProfile)
        ) {
            newErrors.linkedInProfile = 'Must be a valid URL.';
        }
        if (profileData.website && !isValidURL(profileData.website)) {
            newErrors.website = 'Must be a valid URL.';
        }
        if (profileData.twitter && !isValidURL(profileData.twitter)) {
            newErrors.twitter = 'Must be a valid URL.';
        }
        if (profileData.github && !isValidURL(profileData.github)) {
            newErrors.github = 'Must be a valid URL.';
        }
        if (profileData.instagram && !isValidURL(profileData.instagram)) {
            newErrors.instagram = 'Must be a valid URL.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle save action
    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        const formData = new FormData();
        formData.append('firstName', profileData.firstName);
        formData.append('lastName', profileData.lastName);
        formData.append('company', profileData.company);
        formData.append('occupation', profileData.occupation);
        formData.append('linkedInProfile', profileData.linkedInProfile); // <-- renamed
        formData.append('website', profileData.website);
        formData.append('twitter', profileData.twitter);
        formData.append('github', profileData.github);
        formData.append('instagram', profileData.instagram);
        formData.append('translatedLanguages', profileData.translatedLanguages);
        formData.append('availability', profileData.availability);
        if (avatarFile) {
            formData.append('avatar', avatarFile);
        }

        try {
            await axios.put('/api/users/me', formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            setMessage('Profile updated successfully');

            // Refresh data so we get the updated linkedInProfile, etc.
            const refreshed = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const profile = refreshed.data;
            setProfileData({
                _id: profile._id || '',
                email: profile.email || '',
                company: profile.company || '',
                firstName: profile.firstName || '',
                lastName: profile.lastName || '',
                avatarUrl: profile.avatarUrl || '',
                occupation: profile.occupation || '',
                linkedInProfile: profile.linkedInProfile || '',  // <-- use refreshed value
                website: profile.website || '',
                twitter: profile.twitter || '',
                github: profile.github || '',
                instagram: profile.instagram || '',
                translatedLanguages: Array.isArray(profile.translatedLanguages)
                    ? profile.translatedLanguages[0]?.toLowerCase() || ''
                    : profile.translatedLanguages?.toLowerCase() || '',
                availability: profile.availability || '',
            });
            setAvatarFile(null);
            setAvatarPreview('');
            setErrors({});
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage('Error updating profile');
        } finally {
            setSaving(false);
        }
    };

    // Build avatar src: preview takes precedence, then stored avatarUrl
    const avatarSrc = avatarPreview
        ? avatarPreview
        : profileData.avatarUrl
            ? `/api/users/avatars/${profileData._id}/${profileData.avatarUrl.split('/').pop()}`
            : '';

    if (fetching) {
        return (
            <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ marginTop: '50px' }}>
            <Paper elevation={3} sx={styles.paper}>
                {/* Cancel avatar preview button */}
                {avatarPreview && (
                    <IconButton
                        onClick={() => {
                            setAvatarPreview('');
                            setAvatarFile(null);
                        }}
                        sx={styles.cancelIcon}
                        aria-label="Cancel avatar selection"
                    >
                        <Clear />
                    </IconButton>
                )}
                <Typography variant="h4" gutterBottom>
                    Profile Information
                </Typography>

                {/* Avatar Upload & Basic Info */}
                <Box sx={styles.avatarBox}>
                    <Avatar src={avatarSrc} sx={styles.avatarSize} />
                    <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="avatar-upload"
                        type="file"
                        onChange={handleAvatarUpload}
                    />
                    <label htmlFor="avatar-upload">
                        <IconButton
                            color="primary"
                            component="span"
                            aria-label="Upload avatar"
                        >
                            <PhotoCamera />
                        </IconButton>
                    </label>
                </Box>

                <Grid container spacing={2}>
                    {/* Email (read-only) */}
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            fullWidth
                            value={profileData.email}
                            disabled
                        />
                    </Grid>

                    {/* First Name & Last Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="First Name"
                            variant="outlined"
                            fullWidth
                            name="firstName"
                            value={profileData.firstName}
                            onChange={handleChange}
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Last Name"
                            variant="outlined"
                            fullWidth
                            name="lastName"
                            value={profileData.lastName}
                            onChange={handleChange}
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName || ''}
                        />
                    </Grid>

                    {/* Company & Occupation */}
                    <Grid item xs={12}>
                        <TextField
                            label="Company"
                            variant="outlined"
                            fullWidth
                            name="company"
                            value={profileData.company}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Occupation"
                            variant="outlined"
                            fullWidth
                            name="occupation"
                            value={profileData.occupation}
                            onChange={handleChange}
                        />
                    </Grid>

                    {/* Availability */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Availability"
                            variant="outlined"
                            fullWidth
                            name="availability"
                            value={profileData.availability}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>

                {/* Socials Section */}
                <Typography variant="h6" sx={styles.sectionHeader}>
                    Socials
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="LinkedIn Profile"
                            variant="outlined"
                            fullWidth
                            name="linkedInProfile"                    
                            value={profileData.linkedInProfile}        
                            onChange={handleChange}
                            error={Boolean(errors.linkedInProfile)}
                            helperText={errors.linkedInProfile || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Website/Portfolio URL"
                            variant="outlined"
                            fullWidth
                            name="website"
                            value={profileData.website}
                            onChange={handleChange}
                            error={Boolean(errors.website)}
                            helperText={errors.website || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Twitter URL"
                            variant="outlined"
                            fullWidth
                            name="twitter"
                            value={profileData.twitter}
                            onChange={handleChange}
                            error={Boolean(errors.twitter)}
                            helperText={errors.twitter || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="GitHub URL"
                            variant="outlined"
                            fullWidth
                            name="github"
                            value={profileData.github}
                            onChange={handleChange}
                            error={Boolean(errors.github)}
                            helperText={errors.github || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Instagram URL"
                            variant="outlined"
                            fullWidth
                            name="instagram"
                            value={profileData.instagram}
                            onChange={handleChange}
                            error={Boolean(errors.instagram)}
                            helperText={errors.instagram || ''}
                        />
                    </Grid>
                </Grid>

                {/* Language Section */}
                <Typography variant="h6" sx={styles.sectionHeader}>
                    Language
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <FormControl fullWidth error={Boolean(errors.translatedLanguages)}>
                            <InputLabel id="lang-label">Translated Language</InputLabel>
                            <Select
                                labelId="lang-label"
                                id="translatedLanguages"
                                label="Translated Language"
                                name="translatedLanguages"
                                value={profileData.translatedLanguages || ''}
                                onChange={(e) =>
                                    setProfileData({
                                        ...profileData,
                                        translatedLanguages: e.target.value.toLowerCase(),
                                    })
                                }
                            >
                                {isoLanguages.map((lang) => (
                                    <MenuItem key={lang.code} value={lang.code.toLowerCase()}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Flag
                                                code={lang.countryCode}
                                                style={{ marginRight: '10px', width: '20px' }}
                                            />
                                            {lang.name} ({lang.code.toUpperCase()})
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                {/* Save Button */}
                <Box sx={{ marginTop: '30px', position: 'relative' }}>
                    <Button
                        variant="contained"
                        sx={styles.button}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </Box>

                {message && (
                    <Typography sx={{ marginTop: '20px' }} color="success.main">
                        {message}
                    </Typography>
                )}
            </Paper>
        </Container>
    );
};

export default ProfilePage;
